/**
 * Sidebar user chip + popover (Roomgrid pattern).
 *
 * Replaces the old topbar MyUserMenu: a 30px avatar chip pinned to the
 * sidebar footer that opens an upward popover with the full user menu —
 * identity, role, live session countdown, Settings links, the external
 * Hangfire dashboard, the Templatify environment switch, User Profile
 * and Logout.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslate, useGetIdentity, useLogout } from "react-admin";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Popover,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from "@mui/material";
import {
  Person as PersonIcon,
  Logout as LogoutIcon,
  Apps as ApplicationsIcon,
  VpnKey as LicensesIcon,
  Business as ProvidersIcon,
  PriceChange as DefaultPricingIcon,
  Groups as ManageGroupsIcon,
  Dvr as QueuesIcon,
  OpenInNew as OpenInNewIcon,
  UnfoldMore as ChevronIcon,
  ExpandMore as ExpandIcon,
  Tune as ConfigurationIcon,
  TuneOutlined as DefaultParamsIcon,
  Backup as BackupIcon,
} from "@mui/icons-material";
import { decodeJWT, getToken } from "@/utils/auth";
import { useIsSuperUser } from "@/utils/permissions";
import type { AppIdentity } from "@/types/api";
import TemplatifyEnvironmentSelector from "./TemplatifyEnvironmentSelector";

type SettingsLink = { to: string; textKey: string; icon: React.ReactNode };

// The "Configuración" entry opens its own page and also acts as an expandable
// parent grouping the configuration resources below it.
const configParent: SettingsLink = {
  to: "/settings/configuration",
  textKey: "pages.settings.configuration",
  icon: <ConfigurationIcon fontSize="small" />,
};

const configChildren: SettingsLink[] = [
  { to: "/applications", textKey: "resources.applications.name", icon: <ApplicationsIcon fontSize="small" /> },
  { to: "/licenses", textKey: "resources.licenses.name", icon: <LicensesIcon fontSize="small" /> },
  { to: "/providers", textKey: "resources.providers.name", icon: <ProvidersIcon fontSize="small" /> },
  { to: "/default-pricing", textKey: "resources.default-pricing.name", icon: <DefaultPricingIcon fontSize="small" /> },
];

// Settings links that stay flat (outside the Configuración group).
const otherSettingsLinks: SettingsLink[] = [
  { to: "/settings/default-parameters", textKey: "pages.settings.defaultParameters", icon: <DefaultParamsIcon fontSize="small" /> },
  { to: "/manage-groups", textKey: "resources.manage-groups.name", icon: <ManageGroupsIcon fontSize="small" /> },
];

// The Hangfire queues dashboard lives on the notify SERVICE host (not the
// gateway the app talks to). Derive it from VITE_API_URL so it points at the
// right environment without extra config. Opens in a new tab (own Basic auth).
const hangfireDashboardUrl = ((): string => {
  const api = import.meta.env.VITE_API_URL as string | undefined;
  if (api) {
    try {
      const u = new URL(api);
      u.host = u.host.replace("notify-gateway-api", "notify-service-api");
      u.pathname = "/hangfire";
      u.search = "";
      return u.toString();
    } catch {
      /* fall through to the default below */
    }
  }
  // Sin VITE_API_URL, el respaldo es PRODUCCION: un artefacto desplegado al que le
  // falte la variable debe degradar a su propio entorno, nunca mandar al operador a
  // los datos de dev. En local siempre hay .env.development con la URL de dev.
  return "https://notify-service-api.roommatik.com/hangfire";
})();

const useSessionCountdown = (): { label: string; expiring: boolean } => {
  const [label, setLabel] = useState("");
  const [expiring, setExpiring] = useState(false);

  const tick = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLabel("");
      return;
    }
    try {
      const decoded = decodeJWT(token);
      if (!decoded?.exp) {
        setLabel("");
        return;
      }
      const left = decoded.exp - Math.floor(Date.now() / 1000);
      if (left <= 0) {
        setLabel("00:00");
        setExpiring(true);
        return;
      }
      setExpiring(left <= 300);
      const mm = Math.floor(left / 60);
      const ss = left % 60;
      const hh = Math.floor(mm / 60);
      setLabel(
        hh > 0
          ? `${hh}:${String(mm % 60).padStart(2, "0")}:${String(ss).padStart(2, "0")}`
          : `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`,
      );
    } catch {
      setLabel("");
    }
  }, []);

  useEffect(() => {
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [tick]);

  return { label, expiring };
};

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("") || "U";

interface SidebarUserChipProps {
  /** Icon-only rail mode (collapsed sidebar). */
  collapsed?: boolean;
}

export const SidebarUserChip: React.FC<SidebarUserChipProps> = ({
  collapsed = false,
}) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const logout = useLogout();
  const isSuperUser = useIsSuperUser();
  const theme = useTheme();
  const rg = theme.custom.rg;
  const { data: identity } = useGetIdentity();
  const { label: sessionLabel, expiring } = useSessionCountdown();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  const appIdentity = identity as AppIdentity | undefined;
  const userName = appIdentity?.fullName || "User";
  const userRole = (appIdentity?.role || "").toUpperCase();

  const go = (to: string): void => {
    setOpen(false);
    navigate(to);
  };

  const micro = {
    fontFamily: rg.fontUi,
    fontSize: "10.5px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    fontWeight: 600,
    color: rg.text3,
  };

  return (
    <Box sx={{ p: "10px", borderTop: `1px solid ${rg.border}` }}>
      <Box
        ref={anchorRef}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setOpen(true);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "9px",
          p: "7px 8px",
          borderRadius: "10px",
          cursor: "pointer",
          justifyContent: collapsed ? "center" : "flex-start",
          "&:hover": { backgroundColor: rg.bg2 },
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            flex: "none",
            borderRadius: "8px",
            backgroundColor: rg.accentWeak,
            color: rg.accent,
            display: "grid",
            placeItems: "center",
            fontSize: "11.5px",
            fontWeight: 700,
          }}
        >
          {initialsOf(userName)}
        </Box>
        {!collapsed && (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "12.5px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {userName}
              </Typography>
              <Typography className="rg-mono" sx={{ fontSize: "9.5px", letterSpacing: "0.06em", color: rg.text3 }}>
                {userRole}
              </Typography>
            </Box>
            <ChevronIcon
              sx={{ ml: "auto", fontSize: 15, color: rg.text3, flex: "none" }}
            />
          </>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 260, overflow: "hidden" } } }}
      >
        {/* Identity header */}
        <Box
          sx={{
            p: "12px 14px",
            borderBottom: `1px solid ${rg.border}`,
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
            {userName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              component="span"
              className="rg-mono"
              sx={{
                fontSize: "10px",
                fontWeight: 600,
                letterSpacing: "0.05em",
                px: "8px",
                py: "2px",
                borderRadius: "99px",
                backgroundColor: rg.accentWeak,
                color: rg.accent,
              }}
            >
              {userRole}
            </Box>
            {sessionLabel && (
              <Typography
                component="span"
                className="rg-mono"
                sx={{
                  fontSize: "10.5px",
                  color: expiring ? rg.danger.line : rg.text3,
                }}
              >
                {translate("auth.session", { _: "Session" })} {sessionLabel}
              </Typography>
            )}
          </Box>
        </Box>

        {/* User profile */}
        <List dense disablePadding sx={{ p: "6px" }}>
          <ListItemButton
            onClick={() => go("/user-profile")}
            sx={{ borderRadius: "7px", minHeight: 30, py: 0 }}
          >
            <ListItemIcon sx={{ minWidth: 28, color: rg.text3 }}>
              <PersonIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText
              primary={translate("user-profile.pageTitle", { _: "User Profile" })}
              primaryTypographyProps={{ fontSize: "12.5px" }}
            />
          </ListItemButton>
        </List>

        <Divider />

        {/* Settings */}
        <Box sx={{ p: "6px" }}>
          <Typography sx={{ ...micro, px: "9px", py: "5px" }}>
            {translate("pages.settings.title", { _: "Settings" })}
          </Typography>
          <List dense disablePadding>
            {/* Configuración: navigates to its page, and expands the config
                resources grouped beneath it. */}
            <ListItemButton
              onClick={() => go(configParent.to)}
              sx={{ borderRadius: "7px", minHeight: 28, py: 0, pr: "4px" }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 28,
                  color: rg.text3,
                  "& .MuiSvgIcon-root": { fontSize: 16 },
                }}
              >
                {configParent.icon}
              </ListItemIcon>
              <ListItemText
                primary={translate(configParent.textKey, { smart_count: 2, _: "Configuration" })}
                primaryTypographyProps={{ fontSize: "12.5px" }}
              />
              <Box
                component="span"
                role="button"
                aria-label="toggle configuration group"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfigOpen((v) => !v);
                }}
                sx={{
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "6px",
                  p: "2px",
                  "&:hover": { backgroundColor: rg.bg2 },
                }}
              >
                <ExpandIcon
                  sx={{
                    fontSize: 16,
                    color: rg.text3,
                    transition: "transform 0.18s ease",
                    transform: configOpen ? "rotate(0deg)" : "rotate(-90deg)",
                  }}
                />
              </Box>
            </ListItemButton>

            {configOpen &&
              configChildren.map((item) => (
                <ListItemButton
                  key={item.to}
                  onClick={() => go(item.to)}
                  sx={{ borderRadius: "7px", minHeight: 28, py: 0, pl: "18px" }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 28,
                      color: rg.text3,
                      "& .MuiSvgIcon-root": { fontSize: 16 },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={translate(item.textKey, { smart_count: 2 })}
                    primaryTypographyProps={{ fontSize: "12.5px" }}
                  />
                </ListItemButton>
              ))}

            {otherSettingsLinks.map((item) => (
              <ListItemButton
                key={item.to}
                onClick={() => go(item.to)}
                sx={{ borderRadius: "7px", minHeight: 28, py: 0 }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 28,
                    color: rg.text3,
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={translate(item.textKey, { smart_count: 2 })}
                  primaryTypographyProps={{ fontSize: "12.5px" }}
                />
              </ListItemButton>
            ))}

            {/* SuperUser-only DB backup/restore. */}
            {isSuperUser && (
              <ListItemButton
                onClick={() => go("/backup")}
                sx={{ borderRadius: "7px", minHeight: 28, py: 0 }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 28,
                    color: rg.text3,
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                  }}
                >
                  <BackupIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={translate("resources.backup.name", { smart_count: 2 })}
                  primaryTypographyProps={{ fontSize: "12.5px" }}
                />
              </ListItemButton>
            )}
          </List>
        </Box>

        <Divider />

        {/* Hangfire (external) + Templatify environment */}
        <Box sx={{ p: "6px", display: "flex", flexDirection: "column", gap: "4px" }}>
          <ListItemButton
            onClick={() => {
              setOpen(false);
              window.open(hangfireDashboardUrl, "_blank", "noopener,noreferrer");
            }}
            sx={{ borderRadius: "7px", minHeight: 28, py: 0 }}
          >
            <ListItemIcon sx={{ minWidth: 28, color: rg.text3 }}>
              <QueuesIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText
              primary={translate("pages.settings.hangfire", { _: "Job Queues" })}
              primaryTypographyProps={{ fontSize: "12.5px" }}
            />
            <OpenInNewIcon sx={{ fontSize: 13, color: rg.text4, ml: 1 }} />
          </ListItemButton>
          <Box sx={{ px: "9px", pb: "4px" }}>
            <TemplatifyEnvironmentSelector />
          </Box>
        </Box>

        <Divider />

        {/* Logout */}
        <List dense disablePadding sx={{ p: "6px" }}>
          <ListItemButton
            onClick={() => {
              setOpen(false);
              logout();
            }}
            sx={{
              borderRadius: "7px",
              minHeight: 30,
              py: 0,
              color: rg.danger.line,
              "&:hover": { backgroundColor: rg.danger.bg },
            }}
          >
            <ListItemIcon sx={{ minWidth: 28, color: "inherit" }}>
              <LogoutIcon sx={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText
              primary={translate("ra.auth.logout", { _: "Log out" })}
              primaryTypographyProps={{ fontSize: "12.5px", fontWeight: 500 }}
            />
          </ListItemButton>
        </List>
      </Popover>
    </Box>
  );
};

export default SidebarUserChip;
